import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "create-team-page" }
const _hoisted_2 = { class: "container maxWidth-sm" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("h1", { class: "text-center" }, "Create a New Team", -1)
const _hoisted_4 = { class: "create-team-section" }
const _hoisted_5 = { class: "section-title" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("h2", null, "Team Lead", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", { class: "team-create-description" }, " As you’re creating this team, you are automatically assigned Team Lead - a powerful role! You will learn more about the Team Lead role once your team is set up. ", -1)
const _hoisted_8 = { class: "create-team-section" }
const _hoisted_9 = { class: "section-title" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("h2", null, "Choose a name", -1)
const _hoisted_11 = {
  class: "error-feedback text-danger",
  "data-test": "team-input-error"
}
const _hoisted_12 = { key: 0 }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("div", { class: "invalid-feedback" }, null, -1)
const _hoisted_14 = {
  key: 0,
  class: "tagged-teams-container"
}
const _hoisted_15 = { class: "data-tags scrollbar-none" }
const _hoisted_16 = { class: "create-team-section" }
const _hoisted_17 = { class: "section-title" }
const _hoisted_18 = /*#__PURE__*/_createElementVNode("h2", null, "Choose an image", -1)
const _hoisted_19 = {
  key: 0,
  class: "image-grid"
}
const _hoisted_20 = { class: "create-team-image" }
const _hoisted_21 = ["onClick"]
const _hoisted_22 = { class: "text-center" }
const _hoisted_23 = {
  key: 0,
  class: "text-danger",
  "data-test": "submit-team-error"
}

import { maxLength, required } from "@vuelidate/validators";
import { debug as d } from "debug";
import axios, { AxiosError } from "axios";
import { computed, onMounted, ref } from "vue";
import useVuelidate from "@vuelidate/core";
import InfoIcon from "../../components/info-icon.vue";
import StepNumber from "../../components/step-number.vue";
import DataTag from "../../components/data-tag.vue";
import ImageUploader from "../../components/image-uploader.vue";
import CreateTeamModal from "../../components/team/create-team-modal.vue";
import cmsContentMap from "../../services/cms/cms-content-map";
import { CmsImage, CmsTeamImageList } from "../../services/cms/cms-type-definitions";
import cms from "../../services/cms/cms";
import { TeamApiModel, UserTeamsTagDataApiModel } from "../../api-models";
import CmsImg from "../../components/cms/cms-img.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'create-team',
  setup(__props) {

const debug = d("cs.CreateTeam");

const teamNameRegex = /^[\w (),'-]*$/;

const id = ref("");
const name = ref("");
const userTeamTags = ref<UserTeamsTagDataApiModel | null>(null);
const uploadedFile = ref<File | null>(null);
const newTeam = ref<TeamApiModel | null>(null);
const customImageIsSelected = ref(false);
const error = ref("");
const showModal = ref(false);
const imageListDoc = ref<CmsTeamImageList | null>(null);
const chosenImage = ref<CmsImage | null>(null);
const isCreating = ref(false);

const rules = {
  name: { required, maxLength: maxLength(80), regex: (value: string) => teamNameRegex.test(value) },
};

const state = {
  name,
};

const v$ = useVuelidate(rules, state, { $autoDirty: true });

function selectCustomImage(): void {
  customImageIsSelected.value = true;
  chosenImage.value = null;
}

function onFileUpload(file: File) {
  uploadedFile.value = file;
  selectCustomImage();
}

const userTaggedTeams = computed(() => userTeamTags.value?.taggedTeams);

function fetchUserTeamTags() {
  axios
    .get("/api/usertagdata/getteamdata")
    .then((resp) => {
      userTeamTags.value = resp.data;
    })
    .catch((err) => {
      debug(err);
      // do nothing - not a showstopper
    });
}

async function submitWithCustomImage(file: File): Promise<void> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("teamName", name.value);

  try {
    const response = await axios.post<TeamApiModel>("/api/teams/CreateWithImage", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    debug("Upload successful");

    newTeam.value = response.data;
    showModal.value = true;
  } catch (e) {
    const axiosError = e as AxiosError;
    error.value = axiosError?.response?.data ?? axiosError.message;
    isCreating.value = false;
  }
}

onMounted(() => {
  cms.getById("cms-team-image-list", cmsContentMap.settings.teamImageListId).then((doc) => {
    imageListDoc.value = doc;
    chosenImage.value = imageListDoc.value.data.list[0].image;
  });
  fetchUserTeamTags();
});

const imageList = computed(() => imageListDoc.value?.data.list.map((l) => l.image));

function chooseImage(image: CmsImage): void {
  chosenImage.value = image;
  customImageIsSelected.value = false;
}

function cancel(): void {
  window.location.href = "/";
}

function submitWithChosenImage(): void {
  const apiModel = {
    id: id.value,
    name: name.value,
    imageUrl: chosenImage.value?.url,
  } as TeamApiModel;

  axios
    .post("/api/teams/Create", apiModel)
    .then((resp) => {
      newTeam.value = resp.data as TeamApiModel;
      showModal.value = true;
    })
    .catch((err) => {
      debug(err);
      error.value = "There was a problem creating your team. Please try again.";
      isCreating.value = false;
    });
}

async function createTeam(): Promise<void> {
  if (isCreating.value) {
    return;
  }

  if (v$.value.$touch) {
    v$.value.$touch();
  } else {
    throw new Error("Vuelidate this.$v.$touch() unavailable");
  }

  error.value = "";
  if (v$.value.$invalid) {
    error.value = "Please enter a valid team name.";
    return;
  }

  isCreating.value = true;
  if (uploadedFile.value && customImageIsSelected.value) {
    await submitWithCustomImage(uploadedFile.value);
  } else {
    submitWithChosenImage();
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    (newTeam.value)
      ? (_openBlock(), _createBlock(CreateTeamModal, {
          key: 0,
          modelValue: showModal.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showModal).value = $event)),
          team: newTeam.value
        }, null, 8, ["modelValue", "team"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(StepNumber, { width: "40px" }, {
            default: _withCtx(() => [
              _createTextVNode("1")
            ]),
            _: 1
          }),
          _hoisted_6
        ]),
        _hoisted_7
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _createVNode(StepNumber, { width: "40px" }, {
            default: _withCtx(() => [
              _createTextVNode("2")
            ]),
            _: 1
          }),
          _hoisted_10
        ]),
        _createElementVNode("div", {
          style: {"flex-basis":"70%"},
          class: _normalizeClass({ 'has-error': _unref(v$).name.$error })
        }, [
          _withDirectives(_createElementVNode("input", {
            type: "text",
            maxlength: "80",
            "aria-label": "Team name",
            class: "team-input",
            placeholder: "Enter a name for your team",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((name).value = $event)),
            onBlur: _cache[2] || (_cache[2] = ($event: any) => (_unref(v$).name.$touch())),
            "data-test": "team-name-input"
          }, null, 544), [
            [_vModelText, name.value]
          ]),
          _createElementVNode("small", _hoisted_11, [
            _createTextVNode(" Please provide a name for your team"),
            (!_unref(v$).name.regex)
              ? (_openBlock(), _createElementBlock("span", _hoisted_12, " (without special characters)"))
              : _createCommentVNode("", true)
          ]),
          _hoisted_13
        ], 2),
        (userTaggedTeams.value && userTaggedTeams.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createVNode(InfoIcon, {
                "icon-width": "20",
                "tooltip-text": "Suggestions based off Foundations pathway",
                name: "team-suggestion"
              }),
              _createElementVNode("div", _hoisted_15, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(userTaggedTeams.value, (team) => {
                  return (_openBlock(), _createBlock(DataTag, {
                    key: team,
                    "is-selected": name.value === team,
                    onOnTagClick: ($event: any) => (name.value = team),
                    "data-test": "team-suggestion-tag"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(team), 1)
                    ]),
                    _: 2
                  }, 1032, ["is-selected", "onOnTagClick"]))
                }), 128))
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_16, [
        _createElementVNode("div", _hoisted_17, [
          _createVNode(StepNumber, { width: "40px" }, {
            default: _withCtx(() => [
              _createTextVNode("3")
            ]),
            _: 1
          }),
          _hoisted_18
        ]),
        (imageList.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
              _createElementVNode("div", _hoisted_20, [
                _createVNode(ImageUploader, {
                  "is-active": customImageIsSelected.value,
                  onFileUploaded: onFileUpload,
                  onTileSelected: selectCustomImage,
                  ref: "imageUploader"
                }, null, 8, ["is-active"])
              ]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(imageList.value, (image, i) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "create-team-image",
                  key: i
                }, [
                  _createElementVNode("button", {
                    class: _normalizeClass(["team-image-button btn btn-default", { active: chosenImage.value == image }]),
                    onClick: ($event: any) => (chooseImage(image)),
                    "data-test": "team-image",
                    type: "button"
                  }, [
                    _createVNode(CmsImg, { src: image }, null, 8, ["src"])
                  ], 10, _hoisted_21)
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_22, [
        _createElementVNode("div", { style: {"display":"flex","justify-content":"center","margin-bottom":"4rem"} }, [
          _createElementVNode("button", {
            class: "btn btn-default",
            onClick: cancel,
            type: "button"
          }, "Cancel"),
          _createElementVNode("button", {
            class: "btn btn-primary",
            onClick: createTeam,
            style: {"margin-left":"20px"},
            "data-test": "submit-new-team",
            type: "button"
          }, " Create ")
        ]),
        (error.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_23, _toDisplayString(error.value), 1))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})